import { useMemo, type CSSProperties } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { ThemeDefinition } from '@/utils/themeDerivation';
import { hasLowContrast, paletteToCssVars } from '@/utils/themeDerivation';

interface ThemePreviewPanelProps {
  draftTheme: ThemeDefinition;
  previewMode: 'light' | 'dark';
  t: (key: string) => string;
}

export function ThemePreviewPanel({ draftTheme, previewMode, t }: ThemePreviewPanelProps) {
  const palette = previewMode === 'dark' ? draftTheme.dark : draftTheme.light;
  const cssVars = useMemo(() => paletteToCssVars(palette), [palette]);

  const lowContrastDefault = hasLowContrast(palette.textDefault, palette.bgElevated);
  const lowContrastPrimary = hasLowContrast(palette.textPrimary, palette.bgPage);
  const borderColor = previewMode === 'light' ? '#d9d9d9' : '#404040';

  const wrapperStyle = {
    ...cssVars,
    backgroundColor: palette.bgPage,
    '--border': borderColor,
  } as CSSProperties;

  const tableRows = [
    { name: t('themeSettings.previewRowA'), status: t('themeSettings.previewStatusActive'), statusColor: palette.primary },
    { name: t('themeSettings.previewRowB'), status: t('themeSettings.previewStatusPending'), statusColor: palette.textSecondary },
    { name: t('themeSettings.previewRowC'), status: t('themeSettings.previewStatusDone'), statusColor: palette.textPrimary },
  ];

  return (
    <div className="flex flex-col gap-3 h-full">
      <h3 className="text-sm font-semibold text-textDefault">{t('themeSettings.previewTitle')}</h3>

      {(lowContrastDefault || lowContrastPrimary) && (
        <div className="rounded border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          {t('themeSettings.contrastWarning')}
        </div>
      )}

      <div className="rounded-xl border border-border overflow-hidden shadow-sm flex-1" style={wrapperStyle}>
        <div className="flex min-h-[680px]">
          {/* Sidebar */}
          <div className="w-40 shrink-0 p-3 space-y-1.5 border-r border-border" style={{ backgroundColor: palette.bgContainer }}>
            <div className="text-sm font-bold mb-4 px-1" style={{ color: palette.primary }}>
              DQ
            </div>
            <div
              className="rounded-md px-2.5 py-2 text-xs font-medium"
              style={{ backgroundColor: `${palette.primary}22`, color: palette.textPrimary }}
            >
              {t('themeSettings.previewNavActive')}
            </div>
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-md px-2.5 py-2 text-xs" style={{ color: palette.textSecondary }}>
                {t('themeSettings.previewNavItem')}
              </div>
            ))}
          </div>

          {/* Main */}
          <div className="flex-1 flex flex-col min-w-0">
            <div
              className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0"
              style={{ backgroundColor: palette.bgElevated }}
            >
              <span className="text-sm" style={{ color: palette.textSecondary }}>
                {t('themeSettings.previewHeader')}
              </span>
              <div className="w-6 h-6 rounded-full" style={{ backgroundColor: palette.primary }} />
            </div>

            <div className="p-4 flex-1 space-y-4 overflow-auto" style={{ backgroundColor: palette.bgPage }}>
              {/* Card + knappar */}
              <div className="rounded-lg p-4 border border-border" style={{ backgroundColor: palette.bgElevated }}>
                <h4 className="text-sm font-semibold mb-2" style={{ color: palette.textDefault }}>
                  {t('themeSettings.previewCardTitle')}
                </h4>
                <p className="text-xs mb-1" style={{ color: palette.textDefault }}>
                  {t('themeSettings.previewBody')}
                </p>
                <p className="text-xs underline mb-3" style={{ color: palette.textPrimary }}>
                  {t('themeSettings.previewLink')}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rounded-md px-3 py-1.5 text-xs font-medium"
                    style={{ backgroundColor: palette.primary, color: previewMode === 'light' ? '#ffffff' : '#141414' }}
                  >
                    {t('themeSettings.previewPrimaryBtn')}
                  </button>
                  <button
                    type="button"
                    className="rounded-md px-3 py-1.5 text-xs font-medium border"
                    style={{ borderColor: palette.primary, color: palette.primary }}
                  >
                    {t('themeSettings.previewSecondaryBtn')}
                  </button>
                </div>
              </div>

              {/* Flikar */}
              <div className="rounded-lg border border-border overflow-hidden" style={{ backgroundColor: palette.bgElevated }}>
                <p className="text-xs font-semibold uppercase tracking-wide px-4 pt-3 pb-2" style={{ color: palette.textSecondary }}>
                  {t('themeSettings.previewTabsTitle')}
                </p>
                <div className="flex border-b border-border px-2" style={{ borderColor }}>
                  {[t('themeSettings.previewTabOne'), t('themeSettings.previewTabTwo'), t('themeSettings.previewTabThree')].map((label, idx) => (
                    <button
                      key={label}
                      type="button"
                      className="px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors"
                      style={
                        idx === 0
                          ? { borderColor: palette.primary, color: palette.textPrimary }
                          : { borderColor: 'transparent', color: palette.textSecondary }
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="p-4 text-xs" style={{ color: palette.textDefault }}>
                  {t('themeSettings.previewTabContent')}
                </div>
              </div>

              {/* Badges */}
              <div className="rounded-lg p-4 border border-border" style={{ backgroundColor: palette.bgElevated }}>
                <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: palette.textSecondary }}>
                  {t('themeSettings.previewBadgesTitle')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    style={{ backgroundColor: `${palette.primary}22`, color: palette.textPrimary }}
                  >
                    {t('themeSettings.previewBadgePrimary')}
                  </span>
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    style={{ backgroundColor: palette.bgContainer, color: palette.textSecondary }}
                  >
                    {t('themeSettings.previewBadgeNeutral')}
                  </span>
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border"
                    style={{ borderColor: palette.primary, color: palette.primary }}
                  >
                    {t('themeSettings.previewBadgeOutline')}
                  </span>
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    style={{ backgroundColor: `${palette.secondary}33`, color: palette.textDefault }}
                  >
                    {t('themeSettings.previewBadgeSecondary')}
                  </span>
                </div>
              </div>

              {/* Formulär: combobox + input */}
              <div className="rounded-lg p-4 border border-border" style={{ backgroundColor: palette.bgElevated }}>
                <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: palette.textSecondary }}>
                  {t('themeSettings.previewFormTitle')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1" style={{ color: palette.textSecondary }}>
                      {t('themeSettings.previewComboboxLabel')}
                    </label>
                    <div
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                      style={{
                        backgroundColor: palette.bgContainer,
                        borderColor,
                        color: palette.textDefault,
                      }}
                    >
                      <span>{t('themeSettings.previewComboboxValue')}</span>
                      <ChevronDownIcon className="w-4 h-4 shrink-0" style={{ color: palette.textSecondary }} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: palette.textSecondary }}>
                      {t('themeSettings.previewInputLabel')}
                    </label>
                    <div
                      className="rounded-md border px-3 py-2 text-sm"
                      style={{
                        backgroundColor: palette.bgContainer,
                        borderColor,
                        color: palette.textSecondary,
                      }}
                    >
                      {t('themeSettings.previewInputPlaceholder')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabell / grid */}
              <div className="rounded-lg border border-border overflow-hidden" style={{ backgroundColor: palette.bgElevated }}>
                <div className="px-4 py-2.5 border-b border-border" style={{ backgroundColor: palette.bgContainer }}>
                  <p className="text-xs font-semibold" style={{ color: palette.textDefault }}>
                    {t('themeSettings.previewGridTitle')}
                  </p>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ backgroundColor: palette.bgContainer }}>
                      <th className="text-left px-4 py-2 font-medium" style={{ color: palette.textSecondary }}>
                        {t('themeSettings.previewColName')}
                      </th>
                      <th className="text-left px-4 py-2 font-medium" style={{ color: palette.textSecondary }}>
                        {t('themeSettings.previewColStatus')}
                      </th>
                      <th className="text-left px-4 py-2 font-medium" style={{ color: palette.textSecondary }}>
                        {t('themeSettings.previewColDate')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, idx) => (
                      <tr
                        key={row.name}
                        style={{
                          backgroundColor: idx % 2 === 0 ? palette.bgElevated : palette.bgContainer,
                          borderTop: `1px solid ${borderColor}`,
                        }}
                      >
                        <td className="px-4 py-2.5" style={{ color: palette.textDefault }}>
                          {row.name}
                        </td>
                        <td className="px-4 py-2.5 font-medium" style={{ color: row.statusColor }}>
                          {row.status}
                        </td>
                        <td className="px-4 py-2.5" style={{ color: palette.textSecondary }}>
                          2026-05-26
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Modal (mock) */}
              <div className="rounded-lg border border-border overflow-hidden" style={{ backgroundColor: palette.bgElevated }}>
                <p className="text-xs font-semibold uppercase tracking-wide px-4 pt-3 pb-2" style={{ color: palette.textSecondary }}>
                  {t('themeSettings.previewModalTitle')}
                </p>
                <div className="relative mx-4 mb-4 h-40 rounded-lg overflow-hidden">
                  <div
                    className="absolute inset-0"
                    style={{ backgroundColor: previewMode === 'light' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.55)' }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div
                      className="w-full max-w-[280px] rounded-lg border shadow-lg p-4 space-y-3"
                      style={{ backgroundColor: palette.bgElevated, borderColor }}
                    >
                      <h4 className="text-sm font-semibold" style={{ color: palette.textDefault }}>
                        {t('themeSettings.previewModalHeading')}
                      </h4>
                      <p className="text-xs" style={{ color: palette.textSecondary }}>
                        {t('themeSettings.previewModalBody')}
                      </p>
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          type="button"
                          className="rounded-md px-3 py-1.5 text-xs"
                          style={{ color: palette.textSecondary }}
                        >
                          {t('themeSettings.previewModalCancel')}
                        </button>
                        <button
                          type="button"
                          className="rounded-md px-3 py-1.5 text-xs font-medium"
                          style={{ backgroundColor: palette.primary, color: previewMode === 'light' ? '#ffffff' : '#141414' }}
                        >
                          {t('themeSettings.previewModalConfirm')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
